import React, { useState, useEffect } from 'react';
import { TrendingUp, TrendingDown, AlertTriangle, CheckCircle, Home, MapPin, Calendar, DollarSign, Award, Shield, Scale, Building, Clock, Zap, ExternalLink, X, Menu, ArrowLeft, Search, Filter, FileText, Users, Package } from 'lucide-react';
import Papa from 'papaparse';

const Dashboard = () => {
  const [projects, setProjects] = useState([]);
  const [selectedProject, setSelectedProject] = useState(null);
  const [filter, setFilter] = useState('all');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showFilters, setShowFilters] = useState(false);
  
  // Advanced filter states
  const [selectedState, setSelectedState] = useState('all');
  const [selectedDistrict, setSelectedDistrict] = useState('all');
  const [searchProject, setSearchProject] = useState('');
  const [riskFilter, setRiskFilter] = useState('all');
  const [sellabilityFilter, setSellabilityFilter] = useState('all');
  const [inventoryFilter, setInventoryFilter] = useState('all');

  useEffect(() => {
    // Load CSV data with Phase 3 ML predictions
    fetch('/data/phase3_ml_predictions.csv')
      .then(response => {
        if (!response.ok) {
          throw new Error('CSV file not found. Please add phase3_ml_predictions.csv to public/data/ folder');
        }
        return response.text();
      })
      .then(csvText => {
        Papa.parse(csvText, {
          header: true,
          skipEmptyLines: true,
          complete: (results) => {
            if (results.data && results.data.length > 0) {
              const transformedProjects = results.data.map((row, index) => transformProject(row, index));
              setProjects(transformedProjects);
              setLoading(false);
            } else {
              setError('CSV file is empty or invalid');
              setLoading(false);
            }
          },
          error: (error) => {
            console.error('Error parsing CSV:', error);
            setError('Error parsing CSV file: ' + error.message);
            setLoading(false);
          }
        });
      })
      .catch(error => {
        console.error('Error loading CSV:', error);
        setError(error.message);
        setLoading(false);
      });
  }, []);

  const transformProject = (csvRow, index) => {
    // Parse intelligence scores
    const trustScore = parseFloat(csvRow['Project Trust Score']) || 0;
    const credibilityScore = parseFloat(csvRow['Builder Credibility Index']) || 0;
    const costEfficiency = parseFloat(csvRow['Cost Efficiency Score']) || 0;
    const sellabilityScore = parseFloat(csvRow['Sellability Score']) || 0;
    const documentCompliance = parseFloat(csvRow['Document Compliance Score']) || 0;
    const documentFulfillment = parseFloat(csvRow['Document Fulfillment %']) || 0;
    
    // Parse timeline data
    const delayDays = parseInt(csvRow['Delay Days']) || 0;
    const delayMonths = Math.round(delayDays / 30);
    const riskCategory = csvRow['Delivery Risk Category'] || 'Unknown';
    
    // Parse units data (REAL DATA from CSV!)
    const totalUnits = parseInt(csvRow['Total Units']) || 0;
    const soldUnits = parseInt(csvRow['Sold Units']) || 0;
    const remainingUnits = parseInt(csvRow['Remaining Units']) || 0;
    const unitsSoldPercent = parseFloat(csvRow['Units Sold %']) || 0;
    
    // Status classifications
    const sellabilityStatus = csvRow['Sellability Status'] || 'Unknown';
    const inventoryStatus = csvRow['Inventory Status'] || 'Unknown';
    const documentStatus = csvRow['Document Status'] || 'Unknown';
    
    // Parse costs
    const parseAndNormalizeCost = (value) => {
      const num = parseFloat(value) || 0;
      if (num === 0) return 0;
      if (num > 100000) return num / 100000; // Convert rupees to lakhs
      return num;
    };
    
    const totalCostLakhs = parseAndNormalizeCost(csvRow['Total Project Cost']);
    const landCostLakhs = parseAndNormalizeCost(csvRow['Land Cost']);
    const constructionCostLakhs = parseAndNormalizeCost(csvRow['Construction Cost']);
    
    // Determine delivery status
    let deliveryStatus = 'On Track';
    if (riskCategory === 'Very High Risk') deliveryStatus = 'Severely Delayed';
    else if (riskCategory === 'High Risk') deliveryStatus = 'Delayed';
    else if (riskCategory === 'Medium Risk') deliveryStatus = 'Minor Delay';
    else if (riskCategory === 'Low Risk') deliveryStatus = 'On Track';
    
    // Convert trust score to 10-point scale
    const overallRating = (trustScore / 10).toFixed(1);
    
    return {
      id: index + 1,
      name: csvRow['Project Name'] || 'N/A',
      builder: csvRow['Promoter Name'] || 'N/A',
      location: csvRow['District'] || 'N/A',
      reraId: csvRow['Registration No'] || 'N/A',
      
      // Intelligence Scores (Phase 2)
      overallRating: parseFloat(overallRating),
      trustScore: trustScore,
      credibilityScore: credibilityScore,
      costEfficiency: costEfficiency,
      sellabilityScore: sellabilityScore,
      documentCompliance: documentCompliance,
      documentFulfillment: documentFulfillment,
      
      // Phase 3 ML Predictions
      priceAppreciation3Y: parseFloat(csvRow['Price_Appreciation_3Y']) || 0,
      investmentGrade: csvRow['Investment_Grade'] || 'N/A',
      investmentScore: parseFloat(csvRow['Investment_Score']) || 0,
      recommendation: csvRow['Recommendation'] || 'N/A',
      
      // Risk & Status
      riskCategory: riskCategory,
      deliveryStatus: deliveryStatus,
      sellabilityStatus: sellabilityStatus,
      inventoryStatus: inventoryStatus,
      documentStatus: documentStatus,
      
      // Real Units Data
      totalUnits: totalUnits,
      soldUnits: soldUnits,
      remainingUnits: remainingUnits,
      unitsSoldPercent: unitsSoldPercent,
      
      // Financial Data
      valuation: totalCostLakhs * 100000,
      landCost: landCostLakhs * 100000,
      constructionCost: constructionCostLakhs * 100000,
      totalCostLakhs: totalCostLakhs,
      landCostLakhs: landCostLakhs,
      constructionCostLakhs: constructionCostLakhs,
      
      // Timeline Data
      delayDays: delayDays,
      delayMonths: delayMonths,
      proposedEndDate: csvRow['Proposed End Date'] || 'N/A',
      extendedEndDate: csvRow['Extended End Date'] || null,
      actualStartDate: csvRow['Actual Start Date'] || 'N/A',
      
      // Project Details
      projectStatus: csvRow['Construction Status'] || 'Ongoing',
      projectType: csvRow['Project Type'] || 'N/A',
      onScheduleFlag: csvRow['On Schedule Flag'] === 'True' || csvRow['On Schedule Flag'] === 'TRUE',
      district: csvRow['District'] || 'N/A',
      state: 'Madhya Pradesh',
      email: csvRow['Email'] || 'N/A',
      documentCount: parseInt(csvRow['Document Count']) || 0,
      
      // Scores for breakdown display
      transparency: (trustScore / 100) * 10,
      legalScore: (credibilityScore / 100) * 10,
      financialHealth: (costEfficiency / 25) * 10,
      buildQuality: parseFloat(overallRating),
      
      // Estimated appreciation based on intelligence scores
      appreciation: trustScore > 70 ? (15 + Math.random() * 10) : (5 + Math.random() * 8),
      priceChange: sellabilityScore > 70 ? (8 + Math.random() * 7) : (2 + Math.random() * 5),
      
      // Average price per unit (if units exist)
      avgPrice: totalUnits > 0 && totalCostLakhs > 0 
        ? Math.round((totalCostLakhs * 100000) / totalUnits) 
        : 5000000
    };
  };

  const getRatingColor = (rating) => {
    if (rating >= 8.5) return '#10b981';
    if (rating >= 7) return '#f59e0b';
    return '#ef4444';
  };

  const getStatusColor = (status) => {
    const statusMap = {
      'On Track': '#10b981',
      'Minor Delay': '#f59e0b',
      'Delayed': '#ef4444',
      'Severely Delayed': '#dc2626'
    };
    return statusMap[status] || '#6b7280';
  };

  const getRiskColor = (risk) => {
    const riskMap = {
      'Low Risk': '#10b981',
      'Medium Risk': '#f59e0b',
      'High Risk': '#ef4444',
      'Very High Risk': '#dc2626'
    };
    return riskMap[risk] || '#6b7280';
  };

  const getSellabilityColor = (status) => {
    const sellabilityMap = {
      'High Demand': '#10b981',
      'Good Demand': '#059669',
      'Moderate Demand': '#f59e0b',
      'Low Demand': '#f97316',
      'Very Low Demand': '#ef4444'
    };
    return sellabilityMap[status] || '#6b7280';
  };

  const getInventoryColor = (status) => {
    const inventoryMap = {
      'Sold Out': '#10b981',
      'Nearly Sold Out': '#059669',
      'Low Inventory': '#f59e0b',
      'Medium Inventory': '#f97316',
      'High Inventory': '#ef4444'
    };
    return inventoryMap[status] || '#6b7280';
  };

  const getInvestmentGradeColor = (grade) => {
    if (grade.startsWith('A+')) return '#10b981';  // Excellent - Green
    if (grade.startsWith('A')) return '#059669';    // Very Good - Dark Green
    if (grade.startsWith('B+')) return '#3b82f6';   // Good - Blue
    if (grade.startsWith('B')) return '#f59e0b';    // Fair - Orange
    if (grade.startsWith('C')) return '#f97316';    // Below Average - Dark Orange
    if (grade.startsWith('D')) return '#ef4444';    // Poor - Red
    return '#6b7280';  // Unknown - Gray
  };

  // Get unique values for filters
  const uniqueStates = ['all', ...new Set(projects.map(p => p.state).filter(s => s && s !== 'N/A'))];
  const uniqueDistricts = selectedState === 'all' 
    ? ['all', ...new Set(projects.map(p => p.district).filter(d => d && d !== 'N/A'))]
    : ['all', ...new Set(projects.filter(p => p.state === selectedState).map(p => p.district).filter(d => d && d !== 'N/A'))];
  const uniqueRisks = ['all', 'Low Risk', 'Medium Risk', 'High Risk', 'Very High Risk'];
  const uniqueSellability = ['all', 'High Demand', 'Good Demand', 'Moderate Demand', 'Low Demand', 'Very Low Demand'];
  const uniqueInventory = ['all', 'Sold Out', 'Nearly Sold Out', 'Low Inventory', 'Medium Inventory', 'High Inventory'];

  // Enhanced filtering logic
  const filteredProjects = projects.filter(p => {
    // State filter
    if (selectedState !== 'all' && p.state !== selectedState) return false;
    
    // District filter
    if (selectedDistrict !== 'all' && p.district !== selectedDistrict) return false;
    
    // Project name search
    if (searchProject && !p.name.toLowerCase().includes(searchProject.toLowerCase())) return false;
    
    // Risk filter
    if (riskFilter !== 'all' && p.riskCategory !== riskFilter) return false;
    
    // Sellability filter
    if (sellabilityFilter !== 'all' && p.sellabilityStatus !== sellabilityFilter) return false;
    
    // Inventory filter
    if (inventoryFilter !== 'all' && p.inventoryStatus !== inventoryFilter) return false;
    
    // Quick filters
    if (filter === 'high-rated' && p.overallRating < 8) return false;
    if (filter === 'on-track' && p.riskCategory !== 'Low Risk') return false;
    if (filter === 'high-demand' && !['High Demand', 'Good Demand'].includes(p.sellabilityStatus)) return false;
    if (filter === 'sold-out' && !['Sold Out', 'Nearly Sold Out'].includes(p.inventoryStatus)) return false;
    
    return true;
  });

  // Clear all filters
  const clearFilters = () => {
    setSelectedState('all');
    setSelectedDistrict('all');
    setSearchProject('');
    setRiskFilter('all');
    setSellabilityFilter('all');
    setInventoryFilter('all');
    setFilter('all');
  };

  const StatCard = ({ icon: Icon, label, value, change, trend, color = '#10b981' }) => (
    <div style={{
      background: 'linear-gradient(135deg, rgba(255,255,255,0.95) 0%, rgba(255,255,255,0.85) 100%)',
      backdropFilter: 'blur(10px)',
      borderRadius: '16px',
      padding: '24px',
      border: '1px solid rgba(0,0,0,0.08)',
      boxShadow: '0 8px 32px rgba(0,0,0,0.08)',
      position: 'relative',
      overflow: 'hidden',
      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
    }}>
      <div style={{
        position: 'absolute',
        top: 0,
        right: 0,
        width: '100px',
        height: '100px',
        background: `radial-gradient(circle, ${color}15 0%, transparent 70%)`,
        borderRadius: '50%',
        transform: 'translate(30%, -30%)'
      }} />
      <div style={{ position: 'relative', zIndex: 1 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px' }}>
          <span style={{ fontSize: '11px', fontWeight: '600', color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.5px' }}>{label}</span>
          <Icon size={20} color={color} strokeWidth={2.5} />
        </div>
        <div style={{ fontSize: '28px', fontWeight: '700', color: '#111827', marginBottom: '6px' }}>{value}</div>
        {change && (
          <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
            {trend === 'up' ? <TrendingUp size={14} color={color} /> : <TrendingDown size={14} color='#ef4444' />}
            <span style={{ fontSize: '13px', fontWeight: '600', color: trend === 'up' ? color : '#ef4444' }}>
              {change}
            </span>
          </div>
        )}
      </div>
    </div>
  );

  const ProjectCard = ({ project }) => (
    <div 
      onClick={() => setSelectedProject(project)}
      style={{
        background: 'linear-gradient(135deg, #ffffff 0%, #fafafa 100%)',
        borderRadius: '20px',
        padding: '24px',
        border: '1px solid rgba(0,0,0,0.08)',
        boxShadow: '0 4px 20px rgba(0,0,0,0.06)',
        cursor: 'pointer',
        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        position: 'relative',
        overflow: 'hidden'
      }}>
      <div style={{
        position: 'absolute',
        top: '16px',
        right: '16px',
        background: getRatingColor(project.overallRating),
        color: 'white',
        padding: '6px 14px',
        borderRadius: '10px',
        fontSize: '15px',
        fontWeight: '700',
        boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
      }}>
        {project.overallRating.toFixed(1)}
      </div>

      <div style={{ marginBottom: '16px', paddingRight: '50px' }}>
        <h3 style={{ fontSize: '20px', fontWeight: '700', color: '#111827', marginBottom: '8px', lineHeight: '1.3' }}>
          {project.name}
        </h3>
        <p style={{ fontSize: '14px', color: '#6b7280', fontWeight: '500', marginBottom: '6px' }}>
          {project.builder}
        </p>
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginTop: '8px' }}>
          <MapPin size={13} color='#10b981' />
          <span style={{ fontSize: '12px', color: '#6b7280' }}>{project.district}</span>
        </div>
        <div style={{ fontSize: '11px', color: '#9ca3af', marginTop: '4px' }}>
          RERA: {project.reraId}
        </div>
      </div>

      {/* Phase 3 ML Predictions */}
      <div style={{ 
        display: 'grid', 
        gridTemplateColumns: '1fr 1fr', 
        gap: '12px',
        marginBottom: '16px'
      }}>
        <div>
          <div style={{ fontSize: '11px', color: '#9ca3af', marginBottom: '4px', fontWeight: '600' }}>3Y Growth</div>
          <div style={{ fontSize: '16px', fontWeight: '700', color: '#10b981', display: 'flex', alignItems: 'center', gap: '4px' }}>
            <TrendingUp size={14} />
            {project.priceAppreciation3Y.toFixed(1)}%
          </div>
        </div>
        <div>
          <div style={{ fontSize: '11px', color: '#9ca3af', marginBottom: '4px', fontWeight: '600' }}>Investment</div>
          <div style={{ 
            fontSize: '14px', 
            fontWeight: '700', 
            color: getInvestmentGradeColor(project.investmentGrade),
            display: 'inline-block',
            background: getInvestmentGradeColor(project.investmentGrade) + '15',
            padding: '4px 8px',
            borderRadius: '6px'
          }}>
            {project.investmentGrade.split(' ')[0]}
          </div>
        </div>
      </div>

      {/* Units Data (Real from CSV!) */}
      <div style={{ 
        background: '#f9fafb',
        padding: '12px',
        borderRadius: '10px',
        marginBottom: '16px'
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
          <div style={{ fontSize: '11px', color: '#6b7280', fontWeight: '600' }}>
            Units: {project.soldUnits}/{project.totalUnits} Sold
          </div>
          <div style={{ fontSize: '11px', color: '#10b981', fontWeight: '700' }}>
            {project.unitsSoldPercent.toFixed(1)}%
          </div>
        </div>
        <div style={{ 
          width: '100%', 
          height: '6px', 
          background: '#e5e7eb', 
          borderRadius: '3px',
          overflow: 'hidden'
        }}>
          <div style={{
            width: `${Math.min(project.unitsSoldPercent, 100)}%`,
            height: '100%',
            background: 'linear-gradient(90deg, #10b981 0%, #059669 100%)',
            transition: 'width 0.5s ease'
          }} />
        </div>
      </div>

      {/* Status Badges */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginBottom: '12px' }}>
        <span style={{
          background: getRiskColor(project.riskCategory) + '15',
          color: getRiskColor(project.riskCategory),
          padding: '4px 10px',
          borderRadius: '6px',
          fontSize: '11px',
          fontWeight: '600',
          display: 'flex',
          alignItems: 'center',
          gap: '4px'
        }}>
          <AlertTriangle size={10} />
          {project.riskCategory}
        </span>
        
        <span style={{
          background: getInventoryColor(project.inventoryStatus) + '15',
          color: getInventoryColor(project.inventoryStatus),
          padding: '4px 10px',
          borderRadius: '6px',
          fontSize: '11px',
          fontWeight: '600'
        }}>
          {project.inventoryStatus}
        </span>

        <span style={{
          background: '#6366f115',
          color: '#6366f1',
          padding: '4px 10px',
          borderRadius: '6px',
          fontSize: '11px',
          fontWeight: '600'
        }}>
          Doc: {project.documentCompliance}%
        </span>
      </div>

      {/* Timeline */}
      <div style={{ 
        marginTop: '12px',
        padding: '10px',
        background: project.delayDays > 0 ? '#fef3c7' : '#f0fdf4',
        borderRadius: '8px',
        border: `1px solid ${project.delayDays > 0 ? '#fbbf24' : '#10b981'}20`
      }}>
        <div style={{ fontSize: '10px', color: '#9ca3af', marginBottom: '3px', fontWeight: '600' }}>
          {project.extendedEndDate ? 'Extended Completion' : 'Proposed Completion'}
        </div>
        <div style={{ fontSize: '13px', color: '#111827', fontWeight: '600' }}>
          {project.extendedEndDate || project.proposedEndDate}
          {project.delayMonths > 0 && (
            <span style={{ color: '#f59e0b', marginLeft: '8px', fontSize: '11px' }}>
              (+{project.delayMonths}M delay)
            </span>
          )}
        </div>
      </div>
    </div>
  );

  // Propsten Logo Component
  const PropstenLogo = () => (
    <div style={{
      display: 'flex',
      alignItems: 'center',
      gap: '12px'
    }}>
      <div style={{
        width: '48px',
        height: '48px',
        background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
        borderRadius: '12px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: '28px',
        fontWeight: '900',
        color: 'white',
        boxShadow: '0 4px 20px rgba(16, 185, 129, 0.4)',
        fontFamily: '"Montserrat", sans-serif',
        letterSpacing: '-1px'
      }}>
        P
      </div>
      <div>
        <div style={{
          fontSize: '24px',
          fontWeight: '800',
          color: '#111827',
          fontFamily: '"Montserrat", sans-serif',
          letterSpacing: '-0.5px'
        }}>
          Propsten
        </div>
        <div style={{
          fontSize: '10px',
          fontWeight: '600',
          color: '#10b981',
          letterSpacing: '0.5px',
          textTransform: 'uppercase'
        }}>
          Intelligence Platform
        </div>
      </div>
    </div>
  );

  const ProjectDetail = ({ project }) => (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: 'rgba(0,0,0,0.5)',
      backdropFilter: 'blur(8px)',
      zIndex: 1000,
      display: 'flex',
      alignItems: 'flex-start',
      justifyContent: 'center',
      padding: '0',
      animation: 'fadeIn 0.3s ease',
      overflowY: 'auto'
    }}>
      <div className="project-modal" style={{
        background: '#ffffff',
        borderRadius: '0',
        width: '100%',
        minHeight: '100vh',
        maxHeight: '100vh',
        overflow: 'auto',
        boxShadow: '0 24px 80px rgba(0,0,0,0.3)',
        animation: 'slideUp 0.3s ease'
      }}>
        {/* Header */}
        <div style={{
          background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
          padding: '24px 20px',
          position: 'sticky',
          top: 0,
          zIndex: 10,
          overflow: 'hidden'
        }}>
          <button
            onClick={() => setSelectedProject(null)}
            style={{
              position: 'absolute',
              top: '16px',
              right: '16px',
              background: 'rgba(255,255,255,0.2)',
              border: 'none',
              color: 'white',
              width: '40px',
              height: '40px',
              borderRadius: '50%',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: '600',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              transition: 'all 0.2s'
            }}
          >
            <X size={24} />
          </button>
          
          <div style={{ position: 'relative', zIndex: 1, paddingRight: '50px' }}>
            <div style={{
              display: 'inline-block',
              background: 'rgba(255,255,255,0.2)',
              padding: '6px 14px',
              borderRadius: '8px',
              marginBottom: '12px'
            }}>
              <span style={{ color: 'white', fontSize: '12px', fontWeight: '600' }}>
                Trust Score: {project.trustScore.toFixed(1)}/100
              </span>
            </div>
            <h2 style={{ fontSize: '28px', fontWeight: '800', color: 'white', marginBottom: '10px', lineHeight: '1.2' }}>
              {project.name}
            </h2>
            <p style={{ fontSize: '16px', color: 'rgba(255,255,255,0.9)', marginBottom: '8px', fontWeight: '500' }}>
              {project.builder}
            </p>
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '10px' }}>
              <MapPin size={16} color='white' />
              <span style={{ fontSize: '14px', color: 'rgba(255,255,255,0.9)' }}>{project.district}, {project.state}</span>
            </div>
            <div style={{ fontSize: '12px', color: 'rgba(255,255,255,0.8)', marginBottom: '8px' }}>
              RERA: {project.reraId}
            </div>
          </div>
        </div>

        {/* Content */}
        <div style={{ padding: '24px 20px' }}>
          
          {/* Intelligence Scores */}
          <div style={{ marginBottom: '24px' }}>
            <h3 style={{ fontSize: '18px', fontWeight: '700', color: '#111827', marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '8px' }}>
              <Zap size={20} color='#10b981' />
              Intelligence Scores
            </h3>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: '12px' }}>
              {[
                { label: 'Trust Score', value: project.trustScore, max: 100, icon: Shield, color: '#10b981' },
                { label: 'Credibility', value: project.credibilityScore, max: 100, icon: Award, color: '#6366f1' },
                { label: 'Cost Efficiency', value: project.costEfficiency, max: 27, icon: DollarSign, color: '#f59e0b' },
                { label: 'Sellability', value: project.sellabilityScore, max: 100, icon: TrendingUp, color: '#8b5cf6' },
                { label: 'Doc Compliance', value: project.documentCompliance, max: 100, icon: FileText, color: '#06b6d4' },
                { label: 'Doc Fulfillment', value: project.documentFulfillment, max: 100, icon: CheckCircle, color: '#10b981' }
              ].map((item, idx) => (
                <div key={idx} style={{ 
                  background: '#f9fafb',
                  padding: '14px',
                  borderRadius: '10px',
                  border: '1px solid #e5e7eb'
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '8px' }}>
                    <item.icon size={14} color={item.color} />
                    <span style={{ fontSize: '11px', fontWeight: '600', color: '#6b7280' }}>
                      {item.label}
                    </span>
                  </div>
                  <div style={{ fontSize: '22px', fontWeight: '700', color: '#111827', marginBottom: '6px' }}>
                    {item.value.toFixed(1)}/{item.max}
                  </div>
                  <div style={{ 
                    width: '100%', 
                    height: '4px', 
                    background: '#e5e7eb', 
                    borderRadius: '2px',
                    overflow: 'hidden'
                  }}>
                    <div style={{
                      width: `${(item.value / item.max) * 100}%`,
                      height: '100%',
                      background: item.color,
                      transition: 'width 0.5s ease'
                    }} />
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Phase 3 ML Predictions */}
          <div style={{
            background: 'linear-gradient(135deg, #fef3c7 0%, #ffffff 100%)',
            padding: '20px',
            borderRadius: '12px',
            border: '1px solid #fbbf2420',
            marginBottom: '20px'
          }}>
            <h3 style={{ fontSize: '16px', fontWeight: '700', color: '#111827', marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '8px' }}>
              <Award size={18} color='#f59e0b' />
              ML Predictions (3 Year Forecast)
            </h3>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
              <div style={{
                background: 'white',
                padding: '16px',
                borderRadius: '10px',
                border: '1px solid #e5e7eb'
              }}>
                <div style={{ fontSize: '11px', color: '#9ca3af', marginBottom: '8px', fontWeight: '600' }}>
                  Price Appreciation (3Y)
                </div>
                <div style={{ 
                  fontSize: '32px', 
                  fontWeight: '800', 
                  color: '#10b981',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  marginBottom: '8px'
                }}>
                  <TrendingUp size={28} />
                  {project.priceAppreciation3Y.toFixed(1)}%
                </div>
                <div style={{ fontSize: '12px', color: '#6b7280' }}>
                  Expected property value growth over next 3 years
                </div>
              </div>

              <div style={{
                background: 'white',
                padding: '16px',
                borderRadius: '10px',
                border: '1px solid #e5e7eb'
              }}>
                <div style={{ fontSize: '11px', color: '#9ca3af', marginBottom: '8px', fontWeight: '600' }}>
                  Investment Grade
                </div>
                <div style={{ 
                  fontSize: '28px', 
                  fontWeight: '800', 
                  color: getInvestmentGradeColor(project.investmentGrade),
                  marginBottom: '8px',
                  display: 'inline-block',
                  background: getInvestmentGradeColor(project.investmentGrade) + '20',
                  padding: '8px 16px',
                  borderRadius: '10px'
                }}>
                  {project.investmentGrade}
                </div>
                <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '12px' }}>
                  AI Recommendation: <span style={{ fontWeight: '700', color: '#111827' }}>{project.recommendation}</span>
                </div>
              </div>
            </div>

            <div style={{ 
              marginTop: '16px',
              padding: '12px',
              background: '#f0fdf4',
              borderRadius: '8px',
              border: '1px solid #10b98130'
            }}>
              <div style={{ fontSize: '11px', color: '#059669', fontWeight: '600', marginBottom: '4px' }}>
                💡 Investment Insight
              </div>
              <div style={{ fontSize: '13px', color: '#111827', lineHeight: '1.5' }}>
                {project.investmentGrade.startsWith('A+') && 'Excellent investment opportunity with strong growth potential and low risk.'}
                {project.investmentGrade.startsWith('A') && !project.investmentGrade.startsWith('A+') && 'Very good investment with reliable returns and manageable risks.'}
                {project.investmentGrade.startsWith('B+') && 'Good investment option with decent growth prospects.'}
                {project.investmentGrade.startsWith('B') && !project.investmentGrade.startsWith('B+') && 'Fair investment, suitable for balanced portfolios.'}
                {project.investmentGrade.startsWith('C') && 'Below average opportunity, requires careful consideration.'}
                {project.investmentGrade.startsWith('D') && 'High risk investment, not recommended for conservative investors.'}
              </div>
            </div>
          </div>

          {/* Units & Sales Performance */}
          <div style={{
            background: 'linear-gradient(135deg, #f0fdf4 0%, #ffffff 100%)',
            padding: '20px',
            borderRadius: '12px',
            border: '1px solid #10b98120',
            marginBottom: '20px'
          }}>
            <h3 style={{ fontSize: '16px', fontWeight: '700', color: '#111827', marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '8px' }}>
              <Users size={18} color='#10b981' />
              Sales Performance
            </h3>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '16px', marginBottom: '16px' }}>
              <div>
                <div style={{ fontSize: '11px', color: '#9ca3af', marginBottom: '4px', fontWeight: '600' }}>
                  Total Units
                </div>
                <div style={{ fontSize: '24px', fontWeight: '700', color: '#111827' }}>
                  {project.totalUnits}
                </div>
              </div>
              <div>
                <div style={{ fontSize: '11px', color: '#9ca3af', marginBottom: '4px', fontWeight: '600' }}>
                  Sold Units
                </div>
                <div style={{ fontSize: '24px', fontWeight: '700', color: '#10b981' }}>
                  {project.soldUnits}
                </div>
              </div>
              <div>
                <div style={{ fontSize: '11px', color: '#9ca3af', marginBottom: '4px', fontWeight: '600' }}>
                  Remaining
                </div>
                <div style={{ fontSize: '24px', fontWeight: '700', color: '#f59e0b' }}>
                  {project.remainingUnits}
                </div>
              </div>
            </div>
            
            <div style={{ marginBottom: '12px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px' }}>
                <span style={{ fontSize: '13px', color: '#6b7280', fontWeight: '600' }}>Sales Progress</span>
                <span style={{ fontSize: '13px', color: '#10b981', fontWeight: '700' }}>
                  {project.unitsSoldPercent.toFixed(1)}%
                </span>
              </div>
              <div style={{ 
                width: '100%', 
                height: '8px', 
                background: '#e5e7eb', 
                borderRadius: '4px',
                overflow: 'hidden'
              }}>
                <div style={{
                  width: `${Math.min(project.unitsSoldPercent, 100)}%`,
                  height: '100%',
                  background: 'linear-gradient(90deg, #10b981 0%, #059669 100%)',
                  transition: 'width 0.5s ease'
                }} />
              </div>
            </div>

            <div style={{ display: 'flex', gap: '8px' }}>
              <div style={{
                flex: 1,
                background: getSellabilityColor(project.sellabilityStatus) + '15',
                padding: '8px 12px',
                borderRadius: '8px',
                border: `1px solid ${getSellabilityColor(project.sellabilityStatus)}30`
              }}>
                <div style={{ fontSize: '10px', color: '#6b7280', marginBottom: '2px' }}>Sellability</div>
                <div style={{ fontSize: '12px', fontWeight: '700', color: getSellabilityColor(project.sellabilityStatus) }}>
                  {project.sellabilityStatus}
                </div>
              </div>
              <div style={{
                flex: 1,
                background: getInventoryColor(project.inventoryStatus) + '15',
                padding: '8px 12px',
                borderRadius: '8px',
                border: `1px solid ${getInventoryColor(project.inventoryStatus)}30`
              }}>
                <div style={{ fontSize: '10px', color: '#6b7280', marginBottom: '2px' }}>Inventory</div>
                <div style={{ fontSize: '12px', fontWeight: '700', color: getInventoryColor(project.inventoryStatus) }}>
                  {project.inventoryStatus}
                </div>
              </div>
            </div>
          </div>

          {/* Risk & Timeline */}
          <div style={{
            background: 'linear-gradient(135deg, #fef3c7 0%, #ffffff 100%)',
            padding: '20px',
            borderRadius: '12px',
            border: '1px solid #fbbf2420',
            marginBottom: '20px'
          }}>
            <h3 style={{ fontSize: '16px', fontWeight: '700', color: '#111827', marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '8px' }}>
              <Clock size={18} color='#f59e0b' />
              Timeline & Risk
            </h3>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: '12px' }}>
              <div>
                <div style={{ fontSize: '11px', color: '#9ca3af', marginBottom: '4px', fontWeight: '600' }}>
                  Delivery Risk
                </div>
                <div style={{ 
                  display: 'inline-block',
                  background: getRiskColor(project.riskCategory) + '20',
                  color: getRiskColor(project.riskCategory),
                  padding: '6px 12px',
                  borderRadius: '8px',
                  fontSize: '14px',
                  fontWeight: '700'
                }}>
                  {project.riskCategory}
                </div>
              </div>
              
              {project.delayDays > 0 && (
                <div>
                  <div style={{ fontSize: '11px', color: '#9ca3af', marginBottom: '4px', fontWeight: '600' }}>
                    Current Delay
                  </div>
                  <div style={{ fontSize: '16px', fontWeight: '700', color: '#ef4444' }}>
                    {project.delayDays} days ({project.delayMonths} months)
                  </div>
                </div>
              )}

              <div>
                <div style={{ fontSize: '11px', color: '#9ca3af', marginBottom: '4px', fontWeight: '600' }}>
                  Start Date
                </div>
                <div style={{ fontSize: '14px', fontWeight: '600', color: '#111827' }}>
                  {project.actualStartDate}
                </div>
              </div>

              <div>
                <div style={{ fontSize: '11px', color: '#9ca3af', marginBottom: '4px', fontWeight: '600' }}>
                  Proposed End Date
                </div>
                <div style={{ fontSize: '14px', fontWeight: '600', color: '#111827' }}>
                  {project.proposedEndDate}
                </div>
              </div>
              
              {project.extendedEndDate && (
                <div>
                  <div style={{ fontSize: '11px', color: '#9ca3af', marginBottom: '4px', fontWeight: '600' }}>
                    Extended End Date
                  </div>
                  <div style={{ fontSize: '14px', fontWeight: '600', color: '#ef4444' }}>
                    {project.extendedEndDate}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Financial Details */}
          <div style={{
            background: 'linear-gradient(135deg, #f9fafb 0%, #ffffff 100%)',
            padding: '20px',
            borderRadius: '12px',
            border: '1px solid #e5e7eb',
            marginBottom: '20px'
          }}>
            <h3 style={{ fontSize: '16px', fontWeight: '700', color: '#111827', marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '8px' }}>
              <DollarSign size={18} color='#6b7280' />
              Financial Details
            </h3>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: '12px' }}>
              <div>
                <div style={{ fontSize: '11px', color: '#9ca3af', marginBottom: '4px', fontWeight: '600' }}>
                  Total Project Cost
                </div>
                <div style={{ fontSize: '18px', fontWeight: '700', color: '#111827' }}>
                  ₹{project.totalCostLakhs.toFixed(2)} Lakhs
                </div>
              </div>
              <div>
                <div style={{ fontSize: '11px', color: '#9ca3af', marginBottom: '4px', fontWeight: '600' }}>
                  Land Cost
                </div>
                <div style={{ fontSize: '14px', fontWeight: '600', color: '#111827' }}>
                  ₹{project.landCostLakhs.toFixed(2)} Lakhs
                </div>
              </div>
              <div>
                <div style={{ fontSize: '11px', color: '#9ca3af', marginBottom: '4px', fontWeight: '600' }}>
                  Construction Cost
                </div>
                <div style={{ fontSize: '14px', fontWeight: '600', color: '#111827' }}>
                  ₹{project.constructionCostLakhs.toFixed(2)} Lakhs
                </div>
              </div>
              {project.totalUnits > 0 && (
                <div>
                  <div style={{ fontSize: '11px', color: '#9ca3af', marginBottom: '4px', fontWeight: '600' }}>
                    Average Price Per Unit
                  </div>
                  <div style={{ fontSize: '14px', fontWeight: '600', color: '#10b981' }}>
                    ₹{(project.avgPrice / 100000).toFixed(2)} Lakhs
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Contact Info */}
          <div style={{
            background: 'linear-gradient(135deg, #eff6ff 0%, #ffffff 100%)',
            padding: '20px',
            borderRadius: '12px',
            border: '1px solid #3b82f620'
          }}>
            <h3 style={{ fontSize: '16px', fontWeight: '700', color: '#111827', marginBottom: '16px' }}>
              Contact Information
            </h3>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: '12px' }}>
              <div>
                <div style={{ fontSize: '11px', color: '#9ca3af', marginBottom: '4px', fontWeight: '600' }}>
                  Email
                </div>
                <div style={{ fontSize: '13px', fontWeight: '600', color: '#3b82f6', wordBreak: 'break-all' }}>
                  {project.email}
                </div>
              </div>
              <div>
                <div style={{ fontSize: '11px', color: '#9ca3af', marginBottom: '4px', fontWeight: '600' }}>
                  Documents Submitted
                </div>
                <div style={{ fontSize: '14px', fontWeight: '600', color: '#111827' }}>
                  {project.documentCount} documents ({project.documentStatus})
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  if (loading) {
    return (
      <div style={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #f0fdf4 0%, #dcfce7 50%, #bbf7d0 100%)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontFamily: '"DM Sans", -apple-system, BlinkMacSystemFont, system-ui, sans-serif',
        padding: '20px'
      }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{
            width: '60px',
            height: '60px',
            border: '4px solid #10b981',
            borderTopColor: 'transparent',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite',
            margin: '0 auto 16px'
          }} />
          <div style={{ fontSize: '18px', fontWeight: '600', color: '#111827' }}>
            Loading Intelligence Data...
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #f0fdf4 0%, #dcfce7 50%, #bbf7d0 100%)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontFamily: '"DM Sans", -apple-system, BlinkMacSystemFont, system-ui, sans-serif',
        padding: '20px'
      }}>
        <div style={{
          textAlign: 'center',
          background: 'white',
          padding: '40px 30px',
          borderRadius: '20px',
          maxWidth: '500px',
          boxShadow: '0 12px 40px rgba(0,0,0,0.1)'
        }}>
          <AlertTriangle size={48} color="#f59e0b" style={{ marginBottom: '16px' }} />
          <h3 style={{ fontSize: '22px', fontWeight: '700', color: '#111827', marginBottom: '12px' }}>
            Unable to Load Projects
          </h3>
          <p style={{ fontSize: '14px', color: '#6b7280', marginBottom: '20px', lineHeight: '1.6' }}>
            {error}
          </p>
          <p style={{ fontSize: '13px', color: '#9ca3af' }}>
            Please make sure your CSV file is located at:<br/>
            <code style={{ background: '#f3f4f6', padding: '4px 8px', borderRadius: '4px', fontSize: '12px' }}>
              public/data/phase3_ml_predictions.csv
            </code>
          </p>
        </div>
      </div>
    );
  }

  return (
    <div style={{
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #f0fdf4 0%, #dcfce7 50%, #bbf7d0 100%)',
      padding: '20px',
      fontFamily: '"DM Sans", -apple-system, BlinkMacSystemFont, system-ui, sans-serif',
      position: 'relative',
      overflow: 'hidden'
    }}>
      {/* Animated Background Elements */}
      <div style={{
        position: 'fixed',
        top: '-10%',
        right: '-5%',
        width: '400px',
        height: '400px',
        background: 'radial-gradient(circle, rgba(16, 185, 129, 0.15) 0%, transparent 70%)',
        borderRadius: '50%',
        animation: 'float 20s ease-in-out infinite',
        zIndex: 0
      }} />

      <div style={{ maxWidth: '1400px', margin: '0 auto', position: 'relative', zIndex: 1 }}>
        {/* Header with Logo and Home Button */}
        <div style={{ marginBottom: '32px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px', flexWrap: 'wrap', gap: '16px' }}>
            <PropstenLogo />
            <a 
              href="/" 
              style={{
                background: 'rgba(255,255,255,0.95)',
                backdropFilter: 'blur(10px)',
                border: '1px solid rgba(0,0,0,0.08)',
                padding: '12px 24px',
                borderRadius: '12px',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                color: '#111827',
                textDecoration: 'none',
                fontSize: '14px',
                fontWeight: '600',
                transition: 'all 0.3s ease',
                cursor: 'pointer',
                boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = '#10b981';
                e.currentTarget.style.color = 'white';
                e.currentTarget.style.transform = 'translateY(-2px)';
                e.currentTarget.style.boxShadow = '0 8px 20px rgba(16, 185, 129, 0.3)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = 'rgba(255,255,255,0.95)';
                e.currentTarget.style.color = '#111827';
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.1)';
              }}
            >
              <Home size={18} />
              <span>Back to Home</span>
            </a>
          </div>
          
          <h1 className="dashboard-title" style={{ 
            fontSize: '36px', 
            fontWeight: '900', 
            background: 'linear-gradient(135deg, #111827 0%, #374151 100%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            marginBottom: '12px',
            lineHeight: '1.1'
          }}>
            AI-powered Intelligence Platform
          </h1>
          <p style={{ 
            fontSize: '16px', 
            color: '#6b7280', 
            fontWeight: '500'
          }}>
            Showing {filteredProjects.length} of {projects.length} Projects with Advanced Analytics
          </p>
        </div>

        {/* ADVANCED FILTERS SECTION */}
        <div style={{
          background: 'white',
          borderRadius: '20px',
          padding: '24px',
          marginBottom: '32px',
          boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
          border: '1px solid rgba(0,0,0,0.08)'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '20px', flexWrap: 'wrap', gap: '12px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <Filter size={20} color='#10b981' />
              <h2 style={{ fontSize: '18px', fontWeight: '700', color: '#111827', margin: 0 }}>
                Advanced Intelligence Filters
              </h2>
            </div>
            {(selectedState !== 'all' || selectedDistrict !== 'all' || searchProject || riskFilter !== 'all' || sellabilityFilter !== 'all' || inventoryFilter !== 'all' || filter !== 'all') && (
              <button
                onClick={clearFilters}
                style={{
                  background: '#ef4444',
                  color: 'white',
                  border: 'none',
                  padding: '8px 16px',
                  borderRadius: '8px',
                  fontSize: '13px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  transition: 'all 0.2s'
                }}
              >
                Clear All Filters
              </button>
            )}
          </div>

          {/* Filter Grid */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
            gap: '16px',
            marginBottom: '20px'
          }}>
            {/* State Filter */}
            <div>
              <label style={{ fontSize: '12px', fontWeight: '600', color: '#6b7280', display: 'block', marginBottom: '8px' }}>
                State
              </label>
              <select
                value={selectedState}
                onChange={(e) => {
                  setSelectedState(e.target.value);
                  setSelectedDistrict('all');
                }}
                style={{
                  width: '100%',
                  padding: '12px',
                  borderRadius: '10px',
                  border: '1px solid #e5e7eb',
                  fontSize: '14px',
                  fontWeight: '500',
                  color: '#111827',
                  background: 'white',
                  cursor: 'pointer'
                }}
              >
                {uniqueStates.map(state => (
                  <option key={state} value={state}>
                    {state === 'all' ? 'All States' : state}
                  </option>
                ))}
              </select>
            </div>

            {/* District Dropdown */}
            <div>
              <label style={{ fontSize: '12px', fontWeight: '600', color: '#6b7280', display: 'block', marginBottom: '8px' }}>
                District
              </label>
              <select
                value={selectedDistrict}
                onChange={(e) => setSelectedDistrict(e.target.value)}
                style={{
                  width: '100%',
                  padding: '12px',
                  borderRadius: '10px',
                  border: '1px solid #e5e7eb',
                  fontSize: '14px',
                  fontWeight: '500',
                  color: '#111827',
                  background: 'white',
                  cursor: 'pointer'
                }}
              >
                {uniqueDistricts.map(district => (
                  <option key={district} value={district}>
                    {district === 'all' ? 'All Districts' : district}
                  </option>
                ))}
              </select>
            </div>

            {/* Risk Category Filter */}
            <div>
              <label style={{ fontSize: '12px', fontWeight: '600', color: '#6b7280', display: 'block', marginBottom: '8px' }}>
                Delivery Risk
              </label>
              <select
                value={riskFilter}
                onChange={(e) => setRiskFilter(e.target.value)}
                style={{
                  width: '100%',
                  padding: '12px',
                  borderRadius: '10px',
                  border: '1px solid #e5e7eb',
                  fontSize: '14px',
                  fontWeight: '500',
                  color: '#111827',
                  background: 'white',
                  cursor: 'pointer'
                }}
              >
                {uniqueRisks.map(risk => (
                  <option key={risk} value={risk}>
                    {risk === 'all' ? 'All Risk Levels' : risk}
                  </option>
                ))}
              </select>
            </div>

            {/* Sellability Filter */}
            <div>
              <label style={{ fontSize: '12px', fontWeight: '600', color: '#6b7280', display: 'block', marginBottom: '8px' }}>
                Sellability
              </label>
              <select
                value={sellabilityFilter}
                onChange={(e) => setSellabilityFilter(e.target.value)}
                style={{
                  width: '100%',
                  padding: '12px',
                  borderRadius: '10px',
                  border: '1px solid #e5e7eb',
                  fontSize: '14px',
                  fontWeight: '500',
                  color: '#111827',
                  background: 'white',
                  cursor: 'pointer'
                }}
              >
                {uniqueSellability.map(sell => (
                  <option key={sell} value={sell}>
                    {sell === 'all' ? 'All Sellability' : sell}
                  </option>
                ))}
              </select>
            </div>

            {/* Inventory Filter */}
            <div>
              <label style={{ fontSize: '12px', fontWeight: '600', color: '#6b7280', display: 'block', marginBottom: '8px' }}>
                Inventory Status
              </label>
              <select
                value={inventoryFilter}
                onChange={(e) => setInventoryFilter(e.target.value)}
                style={{
                  width: '100%',
                  padding: '12px',
                  borderRadius: '10px',
                  border: '1px solid #e5e7eb',
                  fontSize: '14px',
                  fontWeight: '500',
                  color: '#111827',
                  background: 'white',
                  cursor: 'pointer'
                }}
              >
                {uniqueInventory.map(inv => (
                  <option key={inv} value={inv}>
                    {inv === 'all' ? 'All Inventory' : inv}
                  </option>
                ))}
              </select>
            </div>

            {/* Project Search */}
            <div style={{ gridColumn: 'span 2' }}>
              <label style={{ fontSize: '12px', fontWeight: '600', color: '#6b7280', display: 'block', marginBottom: '8px' }}>
                Search Project
              </label>
              <div style={{ position: 'relative' }}>
                <Search size={18} color='#9ca3af' style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)' }} />
                <input
                  type="text"
                  placeholder="Type project name..."
                  value={searchProject}
                  onChange={(e) => setSearchProject(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '12px 12px 12px 40px',
                    borderRadius: '10px',
                    border: '1px solid #e5e7eb',
                    fontSize: '14px',
                    fontWeight: '500',
                    color: '#111827',
                    background: 'white'
                  }}
                />
              </div>
            </div>
          </div>

          {/* Quick Filters */}
          <div style={{ paddingTop: '20px', borderTop: '1px solid #e5e7eb' }}>
            <label style={{ fontSize: '12px', fontWeight: '600', color: '#6b7280', display: 'block', marginBottom: '12px' }}>
              Quick Filters
            </label>
            <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
              {[
                { id: 'all', label: 'All Projects' },
                { id: 'high-rated', label: 'High Trust (8+)' },
                { id: 'on-track', label: 'Low Risk' },
                { id: 'high-demand', label: 'High Demand' },
                { id: 'sold-out', label: 'Nearly Sold Out' }
              ].map(f => (
                <button
                  key={f.id}
                  onClick={() => setFilter(f.id)}
                  style={{
                    background: filter === f.id 
                      ? 'linear-gradient(135deg, #10b981 0%, #059669 100%)' 
                      : 'rgba(255,255,255,0.9)',
                    color: filter === f.id ? 'white' : '#6b7280',
                    border: filter === f.id ? 'none' : '1px solid rgba(0,0,0,0.1)',
                    padding: '10px 20px',
                    borderRadius: '10px',
                    fontSize: '13px',
                    fontWeight: '600',
                    cursor: 'pointer',
                    transition: 'all 0.3s ease',
                    boxShadow: filter === f.id 
                      ? '0 4px 16px rgba(16, 185, 129, 0.3)' 
                      : '0 2px 8px rgba(0,0,0,0.05)',
                    whiteSpace: 'nowrap'
                  }}
                >
                  {f.label}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Stats Overview */}
        <div style={{ 
          display: 'grid', 
          gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', 
          gap: '16px',
          marginBottom: '32px'
        }}>
          <StatCard 
            icon={Home}
            label="Projects"
            value={filteredProjects.length}
            change="Filtered"
            trend="up"
            color="#10b981"
          />
          <StatCard 
            icon={Award}
            label="Avg Trust Score"
            value={`${filteredProjects.length > 0 ? (filteredProjects.reduce((sum, p) => sum + p.trustScore, 0) / filteredProjects.length).toFixed(1) : 0}`}
            change="/100"
            trend="up"
            color="#8b5cf6"
          />
          <StatCard 
            icon={Users}
            label="Avg Sold"
            value={`${filteredProjects.length > 0 ? (filteredProjects.reduce((sum, p) => sum + p.unitsSoldPercent, 0) / filteredProjects.length).toFixed(1) : 0}%`}
            change="Units"
            trend="up"
            color="#f59e0b"
          />
          <StatCard 
            icon={CheckCircle}
            label="Low Risk"
            value={filteredProjects.filter(p => p.riskCategory === 'Low Risk').length}
            change={`${filteredProjects.length > 0 ? Math.round((filteredProjects.filter(p => p.riskCategory === 'Low Risk').length / filteredProjects.length) * 100) : 0}%`}
            trend="up"
            color="#10b981"
          />
        </div>

        {/* Projects Grid */}
        {filteredProjects.length > 0 ? (
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))',
            gap: '20px'
          }}>
            {filteredProjects.map(project => (
              <ProjectCard key={project.id} project={project} />
            ))}
          </div>
        ) : (
          <div style={{
            textAlign: 'center',
            padding: '60px 20px',
            background: 'rgba(255,255,255,0.8)',
            borderRadius: '20px'
          }}>
            <AlertTriangle size={40} color="#f59e0b" style={{ marginBottom: '16px' }} />
            <h3 style={{ fontSize: '20px', fontWeight: '700', color: '#111827', marginBottom: '10px' }}>
              No Projects Found
            </h3>
            <p style={{ fontSize: '14px', color: '#6b7280', marginBottom: '16px' }}>
              Try adjusting your filters
            </p>
            <button
              onClick={clearFilters}
              style={{
                background: '#10b981',
                color: 'white',
                border: 'none',
                padding: '12px 24px',
                borderRadius: '10px',
                fontSize: '14px',
                fontWeight: '600',
                cursor: 'pointer'
              }}
            >
              Clear All Filters
            </button>
          </div>
        )}

        {/* Footer */}
        <div style={{ 
          marginTop: '60px', 
          textAlign: 'center',
          padding: '30px 20px',
          background: 'rgba(255,255,255,0.5)',
          borderRadius: '16px',
          backdropFilter: 'blur(10px)'
        }}>
          <p style={{ fontSize: '13px', color: '#6b7280', marginBottom: '8px' }}>
            Powered by Phase 2 Intelligence Scoring • Trust Scores • Sellability Analysis • Risk Assessment
          </p>
          <p style={{ fontSize: '11px', color: '#9ca3af' }}>
            {projects.length} projects analyzed with AI-driven intelligence
          </p>
        </div>
      </div>

      {/* Project Detail Modal */}
      {selectedProject && <ProjectDetail project={selectedProject} />}

      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700;800;900&display=swap');
        
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        
        @keyframes slideUp {
          from {
            opacity: 0;
            transform: translateY(30px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        
        @keyframes float {
          0%, 100% {
            transform: translateY(0) translateX(0);
          }
          50% {
            transform: translateY(-20px) translateX(10px);
          }
        }
        
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        
        * {
          margin: 0;
          padding: 0;
          box-sizing: border-box;
        }
        
        body {
          overflow-x: hidden;
        }

        /* Mobile Responsive Styles */
        @media (max-width: 768px) {
          .dashboard-title {
            font-size: 28px !important;
          }
          
          .project-modal {
            border-radius: 0 !important;
            max-height: 100vh !important;
            min-height: 100vh !important;
          }
        }

        @media (max-width: 480px) {
          .dashboard-title {
            font-size: 24px !important;
          }
        }
        
        select:focus, input:focus {
          border-color: #10b981 !important;
          outline: none !important;
        }
      `}</style>
    </div>
  );
};

export default Dashboard;
